import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  OutputRefSubscription,
  createComponent,
  inject,
} from '@angular/core';
import { TransitionService } from '@uirouter/core';
import { FtCreateContestModalComponent } from './ft-create-contest-modal.component';

/**
 * WS5 #10 Stage A inc 2y — native port of the AngularJS `ftCreateContestModal`
 * service (account-v2/.../create-contest-modal/createContestModal.service.ts):
 * the same `show(propertyUuid, clonableContests, onCreated, onCreateFailed,
 * theme)` signature, called by the audiences + contests pages.
 *
 * Lifecycle is the v1 one, which DIFFERS from the 2v invitation-share launcher:
 * - `ensureComponent()` lazily creates ONE body-mounted instance and REUSES it
 *   across shows; only the 'done'/'cancel' broadcasts (`closed` here) destroy
 *   it (v1 destroyModal: ftModal.destroy + element.remove + scope.$destroy).
 * - Navigation did NOT destroy it: the v1 service had no $locationChange
 *   handler — only the ftModal directive's `$locationChangeStart` →
 *   `instance.hide()` (hide + snapshot rollback, NO cancel broadcast). The
 *   per-instance `TransitionService.onStart` hook (START, not success — the
 *   v1 listener fired the moment the URL began changing, even for transitions
 *   that later failed or were superseded) therefore calls
 *   `hideForNavigation()` (not teardown), and the hidden instance — with its
 *   once-computed `sourceContests` — is reused by the next show(), exactly
 *   like the surviving v1 `$compile`d element. (v1's `scope.$on("destroy")`
 *   cleanup listened for a custom "destroy" event nobody emits — dead code,
 *   not ported.)
 * - Every show() re-assigns the scope props (the v1 `<` bindings) → setInput
 *   on every show; `open()` is the 'modal-*.show' equivalent (retake/focus).
 *
 * `hide()` mirrors v1 `ftModal.hide` (a hide-without-destroy; no current
 * callers, kept for the v1 surface).
 */
@Injectable({ providedIn: 'root' })
export class CreateContestModalService {
  /** v1 `this.element` — the lazy, reusable single instance. */
  private ref: ComponentRef<FtCreateContestModalComponent> | null = null;
  private closeSub?: OutputRefSubscription;
  private unhookTransition?: Function;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);
  private readonly transitions = inject(TransitionService);

  show(
    propertyUuid: any,
    clonableContests: any,
    onCreated: (contestUuid: string) => void,
    onCreateFailed: () => void,
    theme?: any,
  ): void {
    this.zone.run(() => {
      this.ensureComponent();
      const ref = this.ref!;
      // v1 show(): the scope props are (re-)assigned on EVERY show.
      ref.setInput('propertyUuid', propertyUuid);
      ref.setInput('clonableContests', clonableContests);
      ref.setInput('onCreated', onCreated);
      ref.setInput('onCreateFailed', onCreateFailed);
      ref.setInput('theme', theme);
      ref.changeDetectorRef.detectChanges();
      ref.instance.open();
    });
  }

  /** v1 hide() — ftModal.hide: hide + rollback, instance survives. */
  hide(): void {
    this.zone.run(() => this.ref?.instance.hideForNavigation());
  }

  private ensureComponent(): void {
    if (this.ref) return;
    const ref = createComponent(FtCreateContestModalComponent, {
      environmentInjector: this.environmentInjector,
    });
    this.ref = ref;

    // v1 'modal-*.done'/'modal-*.cancel' → destroyModal.
    this.closeSub = ref.instance.closed.subscribe(() => this.destroyModal());
    // v1 ftModal directive `$locationChangeStart` → hide (NOT destroy); START
    // so failed/superseded transitions hide too, like v1. The listener lived
    // on the directive scope, so it dies with the instance.
    this.unhookTransition = this.transitions.onStart({}, () =>
      ref.instance.hideForNavigation(),
    ) as unknown as Function;

    this.appRef.attachView(ref.hostView);
    document.body.appendChild(ref.location.nativeElement);
    ref.changeDetectorRef.detectChanges();
  }

  /** v1 destroyModal (createContestModal.service.ts:20-30). */
  private destroyModal(): void {
    const ref = this.ref;
    if (!ref) return;
    this.ref = null;
    this.closeSub?.unsubscribe();
    this.closeSub = undefined;
    this.unhookTransition?.();
    this.unhookTransition = undefined;
    this.appRef.detachView(ref.hostView);
    ref.destroy();
    ref.location.nativeElement.remove();
  }
}
